import { supabase } from "@/integrations/supabase/client";

export interface StoryCardData {
  id: string;
  title: string;
  description: string | null;
  cover_url: string | null;
  story_type: string;
  completion: string;
  reading_minutes: number;
  word_count: number;
  view_count: number;
  like_count: number;
  published_at: string | null;
  categories: { name: string; slug: string; accent: string | null } | null;
  content_types: { name: string; slug: string; is_personal: boolean } | null;
  pen_names: { name: string } | null;
  profiles: { username: string; display_name: string; avatar_url: string | null } | null;
  story_tags?: { tags: { id: string; slug: string; name: string } | null }[] | null;
}

export const STORY_CARD_SELECT =
  "id, title, description, cover_url, story_type, completion, reading_minutes, word_count, view_count, like_count, published_at, story_tags(tags(id, slug, name, status, canonical_id)), categories(name, slug, accent), content_types(name, slug, is_personal), pen_names(name), profiles!stories_author_profile_fk(username, display_name, avatar_url)";

export function authorLabel(story: {
  pen_names?: { name: string } | null;
  profiles?: { display_name: string } | null;
}) {
  return story.pen_names?.name ?? story.profiles?.display_name ?? "Unknown author";
}

export async function fetchContentTypes() {
  const { data, error } = await supabase
    .from("content_types")
    .select("id, slug, name, description, is_personal, position")
    .eq("archived", false)
    .order("position");
  if (error) throw error;
  return data ?? [];
}

export async function fetchMyPenNames(userId: string) {
  const { data, error } = await supabase
    .from("pen_names")
    .select("id, name, bio, is_default")
    .eq("user_id", userId)
    .order("created_at");
  if (error) throw error;
  return data ?? [];
}

export async function fetchPublishedStories(options?: {
  limit?: number;
  orderBy?: "published_at" | "like_count" | "view_count" | "updated_at";
  categorySlug?: string;
  contentTypeSlug?: string;
  search?: string;
  /** Restrict to publications originally written in these language codes. */
  languages?: string[];
}) {
  const limit = options?.limit ?? 12;
  const orderBy = options?.orderBy ?? "published_at";

  let select = STORY_CARD_SELECT;
  if (options?.categorySlug) select = select.replace("categories(", "categories!inner(");
  if (options?.contentTypeSlug) select = select.replace("content_types(", "content_types!inner(");

  let query = supabase
    .from("stories")
    .select(select)
    .eq("status", "published")
    .order(orderBy, { ascending: false, nullsFirst: false })
    .limit(limit);

  if (options?.search) {
    query = query.ilike("title", `%${options.search}%`);
  }
  if (options?.categorySlug) {
    query = query.eq("categories.slug", options.categorySlug);
  }
  if (options?.contentTypeSlug) {
    query = query.eq("content_types.slug", options.contentTypeSlug);
  }
  if (options?.languages?.length) {
    query = query.in("original_language", options.languages);
  }

  const { data, error } = await query;
  if (error) throw error;
  return (data ?? []) as unknown as StoryCardData[];
}

export async function fetchCategories() {
  const { data, error } = await supabase
    .from("categories")
    .select("id, slug, name, description, accent")
    .eq("archived", false)
    .order("name");
  if (error) throw error;
  return data ?? [];
}

export async function fetchProfileByUsername(username: string) {
  const { data, error } = await supabase
    .from("profiles")
    .select(
      "id, username, display_name, bio, avatar_url, banner_url, website_url, social_links, pinned_story_id, support_enabled, age_verified, created_at",
    )
    .ilike("username", username)
    .maybeSingle();
  if (error) throw error;
  return data;
}

export async function fetchProfileStats(userId: string) {
  const [followers, following, stories] = await Promise.all([
    supabase.from("follows").select("*", { count: "exact", head: true }).eq("following_id", userId),
    supabase.from("follows").select("*", { count: "exact", head: true }).eq("follower_id", userId),
    supabase
      .from("stories")
      .select("like_count, view_count")
      .eq("author_id", userId)
      .eq("status", "published"),
  ]);

  const published = stories.data ?? [];
  return {
    followers: followers.count ?? 0,
    following: following.count ?? 0,
    published: published.length,
    likes: published.reduce((sum, s) => sum + (s.like_count ?? 0), 0),
    views: published.reduce((sum, s) => sum + (s.view_count ?? 0), 0),
  };
}

export async function fetchAuthorStories(userId: string) {
  const { data, error } = await supabase
    .from("stories")
    .select(STORY_CARD_SELECT)
    .eq("author_id", userId)
    .eq("status", "published")
    .order("published_at", { ascending: false });
  if (error) throw error;
  return (data ?? []) as unknown as StoryCardData[];
}

export async function fetchUnreadNotificationCount(userId: string) {
  const { count, error } = await supabase
    .from("notifications")
    .select("*", { count: "exact", head: true })
    .eq("user_id", userId)
    .is("read_at", null);
  if (error) throw error;
  return count ?? 0;
}

export async function fetchStoryDetail(storyId: string) {
  const { data, error } = await supabase
    .from("stories")
    .select(
      "id, author_id, title, description, cover_url, story_type, completion, status, author_notes, comments_enabled, reading_minutes, word_count, view_count, like_count, comment_count, bookmark_count, published_at, original_language, categories(name, slug), content_types(name, slug, is_personal), pen_names(name, bio), profiles!stories_author_profile_fk(username, display_name, avatar_url, bio, support_enabled)",
    )
    .eq("id", storyId)
    .maybeSingle();
  if (error) throw error;
  return data;
}

export async function fetchChapters(storyId: string) {
  const { data, error } = await supabase
    .from("chapters")
    .select("id, position, title, content, word_count")
    .eq("story_id", storyId)
    .order("position");
  if (error) throw error;
  return data ?? [];
}

export async function fetchStoryComments(storyId: string) {
  const { data, error } = await supabase
    .from("comments")
    .select("id, body, created_at, like_count, removed, author_id, profiles:author_id(username, display_name, avatar_url)")
    .eq("story_id", storyId)
    .order("created_at", { ascending: false })
    .limit(100);
  if (error) throw error;
  return (data ?? []) as unknown as {
    id: string;
    body: string;
    created_at: string;
    like_count: number;
    removed: boolean;
    author_id: string;
    profiles: { username: string; display_name: string; avatar_url: string | null } | null;
  }[];
}

export async function fetchMyStories(userId: string) {
  const { data, error } = await supabase
    .from("stories")
    .select("id, title, description, status, completion, story_type, content_type_id, pen_name_id, privacy_acknowledged_at, cover_url, word_count, updated_at, published_at")
    .eq("author_id", userId)
    .order("updated_at", { ascending: false });
  if (error) throw error;
  return data ?? [];
}

export async function fetchBookmarkedStories(userId: string) {
  const { data, error } = await supabase
    .from("bookmarks")
    .select(`story_id, created_at, stories(${STORY_CARD_SELECT})`)
    .eq("user_id", userId)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return ((data ?? []) as unknown as { stories: StoryCardData | null }[])
    .map((row) => row.stories)
    .filter((story): story is StoryCardData => Boolean(story));
}

export async function fetchFollowingStories(userId: string) {
  const { data: follows, error: followError } = await supabase
    .from("follows")
    .select("following_id")
    .eq("follower_id", userId);
  if (followError) throw followError;
  const ids = (follows ?? []).map((row) => row.following_id);
  if (ids.length === 0) return [] as StoryCardData[];

  const { data, error } = await supabase
    .from("stories")
    .select(STORY_CARD_SELECT)
    .eq("status", "published")
    .in("author_id", ids)
    .order("published_at", { ascending: false })
    .limit(30);
  if (error) throw error;
  return (data ?? []) as unknown as StoryCardData[];
}

export async function fetchNotifications(userId: string) {
  const { data, error } = await supabase
    .from("notifications")
    .select("id, category, title, body, link, read_at, created_at")
    .eq("user_id", userId)
    .order("created_at", { ascending: false })
    .limit(50);
  if (error) throw error;
  return data ?? [];
}

export async function fetchModerationQueue() {
  // submissions.author_id points at auth.users, not profiles, so the author has
  // to be looked up separately — a PostgREST embed here 400s.
  const { data, error } = await supabase
    .from("submissions")
    .select(
      "id, status, submitted_at, story_id, author_id, stories(title, description, word_count, story_type, content_types(name, slug, is_personal))",
    )
    .in("status", ["submitted", "under_review"])
    .order("submitted_at", { ascending: true });
  if (error) throw error;

  const rows = (data ?? []) as unknown as {
    id: string;
    status: string;
    submitted_at: string;
    story_id: string;
    author_id: string;
    stories: {
      title: string;
      description: string | null;
      word_count: number;
      story_type: string;
      content_types: { name: string; slug: string; is_personal: boolean } | null;
    } | null;
  }[];

  const authorIds = Array.from(new Set(rows.map((row) => row.author_id)));
  const authors = new Map<string, { username: string; display_name: string }>();
  if (authorIds.length > 0) {
    const { data: profiles, error: profileError } = await supabase
      .from("profiles")
      .select("id, username, display_name")
      .in("id", authorIds);
    if (profileError) throw profileError;
    for (const profile of profiles ?? []) {
      authors.set(profile.id, {
        username: profile.username,
        display_name: profile.display_name,
      });
    }
  }

  return rows.map((row) => ({ ...row, profiles: authors.get(row.author_id) ?? null }));
}


export async function fetchOpenReports() {
  const { data, error } = await supabase
    .from("reports")
    .select("id, target_type, reason, details, status, created_at")
    .in("status", ["open", "in_review"])
    .order("created_at", { ascending: true });
  if (error) throw error;
  return data ?? [];
}
